const asObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : null;

const asArray = (value) => (Array.isArray(value) ? value : null);

export const idSuffix = (id) => {
  const text = String(id ?? '').trim();
  if (!text) return '';
  const hex = text.match(/-([0-9a-f]{6})$/i);
  if (hex) return hex[1].toLowerCase();
  const digits = text.match(/-(\d{3,})$/);
  if (digits) return digits[1];
  return text.toLowerCase();
};

export const idsLooselyEqual = (a, b) => {
  if (a == null || b == null) return false;
  if (String(a) === String(b)) return true;
  const left = idSuffix(a);
  const right = idSuffix(b);
  return Boolean(left) && left === right;
};

export const panelSideCount = (side) => {
  const obj = asObject(side);
  if (!obj) return 0;
  if (Array.isArray(obj.points)) return obj.points.length;
  if (Array.isArray(obj.selected)) return obj.selected.length;
  return 0;
};

export const panelGroupCounts = (panelGroup) => {
  const obj = asObject(panelGroup);
  if (!obj) return { hCount: 0, vCount: 0 };
  return {
    hCount: panelSideCount(obj.horizontal),
    vCount: panelSideCount(obj.vertical)
  };
};

export const storageIndexToCanvasIndex = (storageIndex, hCount, vCount) => {
  if (storageIndex >= vCount) return storageIndex - vCount;
  return storageIndex + hCount;
};

export const canvasIndicesFromStorageBits = (bits, hCount, vCount) => {
  const indices = [];
  bits.forEach((bit, storageIndex) => {
    if (Number(bit) > 0) {
      indices.push(storageIndexToCanvasIndex(storageIndex, hCount, vCount));
    }
  });
  return indices;
};

export const canvasIndicesToStorageCsv = (
  canvasIndices,
  hCount,
  vCount
) => {
  const selected = new Set(
    (Array.isArray(canvasIndices) ? canvasIndices : [])
      .map((value) => Number(value))
      .filter(Number.isFinite)
  );
  return {
    vertical_selected: Array.from({ length: vCount }, (_, index) =>
      selected.has(index + hCount) ? 1 : 0
    ).join(','),
    horizontal_selected: Array.from({ length: hCount }, (_, index) =>
      selected.has(index) ? 1 : 0
    ).join(',')
  };
};

const intIndexes = (value) => {
  if (!Array.isArray(value)) return [];
  const out = [];
  const seen = new Set();
  value.forEach((item) => {
    const index = Number(item);
    if (!Number.isFinite(index) || index < 0 || seen.has(index)) return;
    seen.add(index);
    out.push(index);
  });
  return out;
};

const parseCsvBits = (raw, count) => {
  if (raw == null || count <= 0) return null;
  if (Array.isArray(raw)) {
    const bits = Array.from({ length: count }, (_, index) =>
      Number(raw[index]) > 0 ? 1 : 0
    );
    return bits.some((bit) => bit > 0) ? bits : null;
  }
  const text = String(raw).trim();
  if (!text) return null;
  const parts = text.split(',').map((part) => part.trim());
  if (!parts.some((part) => Number(part) > 0)) return null;
  return Array.from({ length: count }, (_, index) =>
    Number(parts[index]) > 0 ? 1 : 0
  );
};

const lookupBits = (selectedPanels, segmentId) => {
  if (Array.isArray(selectedPanels[segmentId])) return selectedPanels[segmentId];
  const match = Object.keys(selectedPanels).find((key) =>
    idsLooselyEqual(key, segmentId)
  );
  return match && Array.isArray(selectedPanels[match])
    ? selectedPanels[match]
    : null;
};

export const hasCurrentProposalSelection = (proposal) => {
  const designData = asObject(proposal?.design_data);
  const raw =
    designData?.selectedPanelsPerRoof || designData?.selected_panels_per_roof;
  return Boolean(asObject(raw));
};

export const selectedPanelsPerRoofFromStringing = (stringingData) => {
  const out = {};
  const rows = asArray(stringingData);
  if (!rows) return out;
  rows.forEach((entry) => {
    const panels = asArray(asObject(entry)?.panels);
    if (!panels) return;
    panels.forEach((pair) => {
      if (!Array.isArray(pair) || pair.length < 2) return;
      const roof = Number(pair[0]);
      const panel = Number(pair[1]);
      if (!Number.isFinite(roof) || !Number.isFinite(panel) || panel < 0) return;
      if (!Array.isArray(out[roof])) out[roof] = [];
      if (!out[roof].includes(panel)) out[roof].push(panel);
    });
  });
  Object.keys(out).forEach((key) => out[Number(key)].sort((a, b) => a - b));
  return out;
};

const canvasIndexForPayloadRoof = (payloadRoof, panels) => {
  const payloadId = payloadRoof?.id;
  return panels.findIndex((panel) => idsLooselyEqual(panel?.id, payloadId));
};

const selectedPanelsPerRoofFromProductionMap = (proposal, panels) => {
  const designData = asObject(proposal?.design_data) || {};
  const production = asObject(designData.production) || {};
  const raw =
    production.selected_panels_per_roof_canvas ||
    production.selected_panels_per_roof ||
    production.selectedPanelsPerRoof;
  if (!asObject(raw)) return {};
  const payloadRoofs = asArray(asObject(designData.production_payload)?.roofs);
  const out = {};
  Object.keys(raw).forEach((key) => {
    const payloadIndex = Number(key);
    if (!Number.isFinite(payloadIndex)) return;
    let canvasRoof = payloadIndex;
    if (payloadRoofs?.[payloadIndex]) {
      const mapped = canvasIndexForPayloadRoof(
        payloadRoofs[payloadIndex],
        panels
      );
      if (mapped >= 0) canvasRoof = mapped;
    }
    const indexes = intIndexes(raw[key]);
    if (indexes.length > 0) out[canvasRoof] = indexes;
  });
  return out;
};

const selectedPanelsPerRoofFromProductionPerPanel = (proposal, panels) => {
  const designData = asObject(proposal?.design_data) || {};
  const entries =
    asArray(designData.productionPerPanel) ||
    asArray(designData.production_per_panel) ||
    asArray(asObject(designData.production)?.production_per_panel) ||
    [];
  if (!entries.length) return {};
  const payloadRoofs = asArray(asObject(designData.production_payload)?.roofs);
  const out = {};
  entries.forEach((entry, fallbackIndex) => {
    const obj = asObject(entry);
    const roofPanels = asArray(obj?.roofPanels);
    if (!obj || !roofPanels) return;
    const payloadIndex = Number.isFinite(Number(obj.idx))
      ? Number(obj.idx)
      : fallbackIndex;
    let canvasRoof = payloadIndex;
    if (payloadRoofs?.[payloadIndex]) {
      const mapped = canvasIndexForPayloadRoof(
        payloadRoofs[payloadIndex],
        panels
      );
      if (mapped >= 0) canvasRoof = mapped;
    }
    const indexes = roofPanels
      .map((value, panelIndex) => (Number(value) > 0 ? panelIndex : -1))
      .filter((panelIndex) => panelIndex >= 0);
    if (indexes.length > 0) out[canvasRoof] = indexes;
  });
  return out;
};

const selectedPanelsPerRoofFromDesignCsv = (proposal, panels) => {
  const designs = asArray(proposal?.designs) || [];
  const designMap = asObject(asObject(proposal?.design_data)?.design_map) || {};
  const out = {};
  designs.forEach((design) => {
    const row = asObject(design);
    if (!row?.id) return;
    const mappedSegmentId = designMap[String(row.id)];
    const canvasIndex = panels.findIndex((panel) =>
      idsLooselyEqual(panel?.id, mappedSegmentId || row.id)
    );
    if (canvasIndex < 0) return;
    const { hCount, vCount } = panelGroupCounts(panels[canvasIndex]);
    const verticalBits = parseCsvBits(row.vertical_selected, vCount);
    const horizontalBits = parseCsvBits(row.horizontal_selected, hCount);
    if (!verticalBits && !horizontalBits) return;
    const storageBits = [
      ...(verticalBits || Array(vCount).fill(0)),
      ...(horizontalBits || Array(hCount).fill(0))
    ];
    const indices = canvasIndicesFromStorageBits(
      storageBits,
      hCount,
      vCount
    );
    if (indices.length > 0) out[canvasIndex] = indices;
  });
  return out;
};

const countSelected = (selected) =>
  Object.values(selected).reduce(
    (total, indexes) => total + (Array.isArray(indexes) ? indexes.length : 0),
    0
  );

export const deriveCanvasSelectedPanelsPerRoof = (proposal) => {
  const designData = asObject(proposal?.design_data) || {};
  const panels = asArray(designData.panels) || [];
  if (hasCurrentProposalSelection(proposal)) {
    const raw =
      designData.selectedPanelsPerRoof || designData.selected_panels_per_roof;
    const out = {};
    Object.keys(raw).forEach((key) => {
      const roof = Number(key);
      if (!Number.isFinite(roof)) return;
      const indexes = intIndexes(raw[key]);
      if (indexes.length > 0) out[roof] = indexes;
    });
    return out;
  }
  const fromStringing = selectedPanelsPerRoofFromStringing(
    designData.stringing_data
  );
  if (countSelected(fromStringing) > 0) return fromStringing;
  const fromProductionMap = selectedPanelsPerRoofFromProductionMap(
    proposal,
    panels
  );
  if (countSelected(fromProductionMap) > 0) return fromProductionMap;
  const fromPerPanel = selectedPanelsPerRoofFromProductionPerPanel(
    proposal,
    panels
  );
  if (countSelected(fromPerPanel) > 0) return fromPerPanel;
  return selectedPanelsPerRoofFromDesignCsv(proposal, panels);
};

export const buildHomeownerSelectionState = (
  selectedPerRoof,
  segments,
  panels
) => {
  const selectedPanels = {};
  const selectedObjectsPanels = {};
  const count = Math.max(segments.length, panels.length);
  for (let index = 0; index < count; index += 1) {
    const segmentId = String(
      segments[index]?.id ?? panels[index]?.id ?? index
    );
    const { hCount, vCount } = panelGroupCounts(panels[index]);
    const indexes = Array.isArray(selectedPerRoof[index])
      ? selectedPerRoof[index]
      : [];
    const maxSelected = indexes.reduce(
      (max, value) => Math.max(max, Number(value)),
      -1
    );
    const total = Math.max(
      hCount + vCount,
      Number.isFinite(maxSelected) ? maxSelected + 1 : 0,
      0
    );
    const bits = Array(total).fill(0);
    indexes.forEach((panelIndex) => {
      const normalized = Number(panelIndex);
      if (
        Number.isFinite(normalized) &&
        normalized >= 0 &&
        normalized < total
      ) {
        bits[normalized] = 1;
      }
    });
    selectedPanels[segmentId] = bits;
    if (indexes.length > 0) selectedObjectsPanels[index] = [...indexes];
  }
  return { selectedPanels, selectedObjectsPanels };
};

export const canvasSelectionFromHomeownerBits = (
  selectedPanels,
  segments,
  panels
) => {
  const out = {};
  if (!selectedPanels) return out;
  const count = Math.max(segments?.length || 0, panels?.length || 0);
  for (let index = 0; index < count; index += 1) {
    const segmentId = String(
      segments?.[index]?.id ?? panels?.[index]?.id ?? ''
    );
    const bits =
      lookupBits(selectedPanels, segmentId) ||
      (Array.isArray(selectedPanels[String(index)])
        ? selectedPanels[String(index)]
        : null);
    if (!Array.isArray(bits)) continue;
    const indexes = bits
      .map((bit, panelIndex) => (Number(bit) > 0 ? panelIndex : -1))
      .filter((panelIndex) => panelIndex >= 0);
    if (indexes.length > 0) out[index] = indexes;
  }
  return out;
};

export const homeownerBitsFromCanvasSelection = (
  canvasPanels,
  selectedPanelsTemplate,
  segments,
  panels
) => {
  const next = {};
  const count = Math.max(segments.length, panels?.length || 0);
  for (let index = 0; index < count; index += 1) {
    const segmentId = String(
      segments[index]?.id ?? panels?.[index]?.id ?? ''
    );
    const counts = panelGroupCounts(panels?.[index]);
    const template =
      lookupBits(selectedPanelsTemplate, segmentId) ||
      Array(counts.hCount + counts.vCount).fill(0);
    const bits = Array(template.length).fill(0);
    const indexes = canvasPanels[index] || [];
    indexes.forEach((panelIndex) => {
      const normalized = Number(panelIndex);
      if (
        Number.isFinite(normalized) &&
        normalized >= 0 &&
        normalized < bits.length
      ) {
        bits[normalized] = 1;
      }
    });
    if (segmentId) next[segmentId] = bits;
  }
  return next;
};

const parseMonthly = (value) => {
  if (Array.isArray(value) && value.length >= 12) {
    return value.slice(0, 12).map((item) => Number(item) || 0);
  }
  if (typeof value === 'string' && value.includes(',')) {
    const parts = value.split(',').map((part) => Number(part.trim()) || 0);
    return parts.length >= 12 ? parts.slice(0, 12) : null;
  }
  return null;
};

export const readPersistedProductionStats = (proposal, selectedCount) => {
  const designData = asObject(proposal?.design_data) || {};
  const summary = asObject(designData.summary) || {};
  const production = asObject(designData.production) || {};
  const monthly =
    parseMonthly(designData.monthlyProduction) ||
    parseMonthly(designData.monthly_production) ||
    parseMonthly(production.monthly_kwh) ||
    Array(12).fill(0);
  const solarProduction = Number(
    summary.solarProduction ??
      summary.solar_production ??
      designData.total_annual_kwh ??
      production.annual_kwh ??
      monthly.reduce((sum, value) => sum + value, 0)
  );
  const totalPanels = Number(
    selectedCount ||
      summary.totalPanels ||
      summary.total_panels ||
      designData.selected_panels ||
      0
  );
  const systemSize = Number(
    summary.systemSize ??
      summary.system_size ??
      designData.system_size_kw ??
      production.system_size_kw ??
      0
  );
  const annualUsage = Number(summary.annualUsage ?? summary.annual_usage ?? 0);
  const offset = Number(
    summary.offset ??
      (annualUsage > 0 && solarProduction > 0
        ? (solarProduction / annualUsage) * 100
        : 0)
  );
  return {
    totalPanels,
    systemSize,
    solarProduction: Number.isFinite(solarProduction) ? solarProduction : 0,
    monthlyProduction: monthly,
    offset: Number.isFinite(offset) ? offset : 0,
    annualUsage: Number.isFinite(annualUsage) ? annualUsage : 0
  };
};

export const applySelectionToPanelGroups = (panels, selectedPerRoof) =>
  panels.map((panel, index) => {
    const group = asObject(panel);
    if (!group) return panel;
    const horizontal = { ...(asObject(group.horizontal) || {}) };
    const vertical = { ...(asObject(group.vertical) || {}) };
    const hCount = panelSideCount(horizontal);
    const vCount = panelSideCount(vertical);
    const selected = new Set(intIndexes(selectedPerRoof[index]));
    return {
      ...group,
      horizontal: {
        ...horizontal,
        selected: Array.from({ length: hCount }, (_, panelIndex) =>
          selected.has(panelIndex)
        )
      },
      vertical: {
        ...vertical,
        selected: Array.from({ length: vCount }, (_, panelIndex) =>
          selected.has(panelIndex + hCount)
        )
      }
    };
  });

export const applyCanvasSelectionToDesigns = (
  designs,
  selectedPerRoof,
  panels,
  designMap
) =>
  designs.map((design) => {
    const row = asObject(design);
    if (!row?.id) return design;
    const mappedSegmentId = designMap?.[String(row.id)];
    const canvasIndex = panels.findIndex((panel) =>
      idsLooselyEqual(panel?.id, mappedSegmentId || row.id)
    );
    if (canvasIndex < 0) return design;
    const { hCount, vCount } = panelGroupCounts(panels[canvasIndex]);
    return {
      ...row,
      ...canvasIndicesToStorageCsv(
        selectedPerRoof[canvasIndex] || [],
        hCount,
        vCount
      )
    };
  });

export const buildCurrentFormatProposalPatch = (
  proposal,
  selectedPerRoof,
  proposalVersion
) => {
  const designData = { ...(asObject(proposal?.design_data) || {}) };
  const panels = asArray(designData.panels) || [];
  const designs = asArray(proposal?.designs) || [];
  const designMap = asObject(designData.design_map);
  const selectedCount = countSelected(selectedPerRoof);
  const nextPanels = applySelectionToPanelGroups(panels, selectedPerRoof);
  const nextDesigns = applyCanvasSelectionToDesigns(
    designs,
    selectedPerRoof,
    nextPanels,
    designMap
  );
  if (
    designData.production_per_panel != null &&
    designData.productionPerPanel == null
  ) {
    designData.productionPerPanel = designData.production_per_panel;
  }
  if (
    designData.monthly_production != null &&
    designData.monthlyProduction == null
  ) {
    designData.monthlyProduction = designData.monthly_production;
  }
  return {
    ...(proposalVersion != null ? { version: proposalVersion } : {}),
    designs: nextDesigns,
    design_data: {
      ...designData,
      panels: nextPanels,
      selectedPanelsPerRoof: selectedPerRoof,
      selected_panels_per_roof: selectedPerRoof,
      selected_panels: selectedCount || designData.selected_panels
    }
  };
};
